import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/FacebookPageAnalyticsPosts.graphql';
import { Query, QueryfacebookPageAnalyticsPostsArgs } from '../../__generated__/globalTypes';

export const useFacebookPageAnalyticsPostsQuery = (
  options?: QueryHookOptions<Query, QueryfacebookPageAnalyticsPostsArgs>
) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryfacebookPageAnalyticsPostsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useFacebookPageAnalyticsPostsLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryfacebookPageAnalyticsPostsArgs>
): QueryResult<Query, QueryfacebookPageAnalyticsPostsArgs> & {
  getFacebookPageAnalyticsPosts: LazyQueryExecFunction<Query, QueryfacebookPageAnalyticsPostsArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getFacebookPageAnalyticsPosts, result] = useLazyQuery<Query, QueryfacebookPageAnalyticsPostsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getFacebookPageAnalyticsPosts, ...result };
};

export const useFacebookPageAnalyticsPostsPromiseQuery = () => {
  const client = useApolloClient();

  const getFacebookPageAnalyticsPosts = useCallback(
    (options?: Omit<QueryOptions<QueryfacebookPageAnalyticsPostsArgs, Query>, 'query'>) =>
      client.query<Query, QueryfacebookPageAnalyticsPostsArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getFacebookPageAnalyticsPosts };
};
// Suspense Query Hooks
export const useFacebookPageAnalyticsPostsSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryfacebookPageAnalyticsPostsArgs>
) => {
  const result = useSuspenseQuery<Query, QueryfacebookPageAnalyticsPostsArgs>(QUERY, {
    ...options
  });

  return result;
};
